// PROTOTYPE — seed estimates for the Estimates list. They are generated from the
// Job 2918 takeoff (scaled / filtered) so every seed opens in the workspace.

import { RAW_TAKEOFF_2918, TAKEOFF_FILE } from "./takeoff.mock";
import { parseRawLine } from "../engine/suggestSurface";
import { buildEstimateLine, calculateEstimate, indexConfig, toTotalsSnapshot } from "../engine/calcMock";

export const JOBS = {
  2918: { jobId: 2918, title: "Hardie Siding Catalog", clientName: "Chotu Lal Sharma", takeoffFile: TAKEOFF_FILE },
  2871: { jobId: 2871, title: "Lakeview Elementary – Summer Repaint", clientName: "Lakeview School District", takeoffFile: null },
  2904: { jobId: 2904, title: "Portage Central HS – Gym Addition", clientName: "Portage Public Schools", takeoffFile: null },
  2856: { jobId: 2856, title: "Kalamazoo Medical Office Building", clientName: "Bronson Health Group", takeoffFile: null },
  2930: { jobId: 2930, title: "Paw Paw District Library – Exterior", clientName: "Village of Paw Paw", takeoffFile: null },
};

export const DEFAULT_PROJECT = {
  projectName: "MATTAWAN MIDDLE SCHOOL – ADDITIONS & RENOVATIONS",
  location: "Mattawan",
  state: "Michigan",
  clientGc: "Mattawan Consolidated School",
  estimator: "",
  bidNumber: "B-2918-01",
  bidDate: "2026-10-28",
  taxExempt: true,
  bondRequired: true,
  brandMode: "MIX",
  interiorCrew: 4,
  exteriorCrew: 3,
  conditionDefault: "New construction",
  difficultyFactor: 1.0,
  contingencyPct: 0.03,
  projectInsurance: 1500,
  gcTemplateId: "gc-default",
};

export const DEFAULT_INCLUSIONS =
  "• Surface preparation per manufacturer's written instructions\n• Prime and finish coats per finish schedule\n• Masking and protection of adjacent finishes\n• Daily cleanup and final cleaning of painting areas";
export const DEFAULT_EXCLUSIONS =
  "• Wall covering, special coatings not listed\n• Painting of pre-finished items\n• Overtime / after-hours work\n• Lead or hazardous material abatement";

const SEEDS = [
  { id: "EST-1001", jobId: 2871, status: "FINALIZED", scale: 0.55, sections: ["wall", "ceiling", "door", "trim"], condition: "Repaint", brand: "SW", project: "LAKEVIEW ELEMENTARY – SUMMER REPAINT", location: "Battle Creek", estimator: "Priya Nair", daysAgo: 21, bond: false },
  { id: "EST-1002", jobId: 2904, status: "DRAFT", scale: 0.8, sections: ["wall", "ceiling", "door"], condition: "New construction", brand: "MIX", project: "PORTAGE CENTRAL HS – GYM ADDITION", location: "Portage", estimator: "Arjun Patel", daysAgo: 6, bond: true },
  { id: "EST-1003", jobId: 2856, status: "FINALIZED", scale: 1.25, sections: ["wall", "ceiling", "door", "trim"], condition: "New construction", brand: "BM", project: "KALAMAZOO MEDICAL OFFICE BUILDING", location: "Kalamazoo", estimator: "Priya Nair", daysAgo: 13, bond: true },
  { id: "EST-1004", jobId: 2930, status: "DRAFT", scale: 1.0, sections: ["exterior", "trim"], condition: "Restoration", brand: "SW", project: "PAW PAW DISTRICT LIBRARY – EXTERIOR", location: "Paw Paw", estimator: "Arjun Patel", daysAgo: 2, bond: false },
];

const isoDaysAgo = (d, hours = 0) => new Date(Date.now() - d * 86400000 - hours * 3600000).toISOString();

/** Auto-maps the takeoff using the suggester (unmapped / no-qty lines dropped). */
export const autoMappedLines = (config, project, { scale = 1, sections } = {}) => {
  const idx = indexConfig(config);
  return RAW_TAKEOFF_2918.map(parseRawLine)
    .filter((l) => l.suggestedSurfaceId && l.qty > 0 && (!sections || sections.includes(l.section)))
    .map((l) => buildEstimateLine({ ...l, qty: +(l.qty * scale).toFixed(2) }, l.suggestedSurfaceId, config, project, idx));
};

/** @returns {import("../types").Estimate[]} */
export const buildSeedEstimates = (config) =>
  SEEDS.map((s) => {
    const job = JOBS[s.jobId];
    const project = {
      ...DEFAULT_PROJECT,
      projectName: s.project,
      location: s.location,
      clientGc: job.clientName,
      estimator: s.estimator,
      bidNumber: `B-${s.jobId}-01`,
      conditionDefault: s.condition,
      brandMode: s.brand,
      bondRequired: s.bond,
      taxExempt: s.jobId !== 2856,
    };
    const lines = autoMappedLines(config, project, { scale: s.scale, sections: s.sections });
    const totals = toTotalsSnapshot(calculateEstimate({ lines, project }, config));
    const created = isoDaysAgo(s.daysAgo, 3);
    const updated = isoDaysAgo(s.daysAgo - (s.status === "FINALIZED" ? 1 : 0.2));
    const history = [
      { id: `${s.id}-h1`, type: "CREATED", user: s.estimator, at: created, totals: { ...totals, bidTotal: totals.bidTotal * 0.96 }, note: "Created from takeoff" },
      { id: `${s.id}-h2`, type: "DRAFT_SAVED", user: s.estimator, at: isoDaysAgo(s.daysAgo - 0.1), totals: { ...totals, bidTotal: totals.bidTotal * 0.985 } },
      { id: `${s.id}-h3`, type: "RECALCULATED", user: s.estimator, at: updated, totals },
    ];
    if (s.status === "FINALIZED") history.push({ id: `${s.id}-h4`, type: "FINALIZED", user: s.estimator, at: updated, totals, note: "Submitted to GC" });
    return {
      id: s.id,
      jobId: s.jobId,
      jobTitle: job.title,
      clientName: job.clientName,
      status: s.status,
      configVersionId: s.daysAgo > 15 ? "v11" : "v12",
      project,
      lines,
      equipmentOverrides: {},
      gcOverrides: {},
      inclusions: DEFAULT_INCLUSIONS,
      exclusions: DEFAULT_EXCLUSIONS,
      createdBy: s.estimator,
      createdAt: created,
      updatedAt: updated,
      lastCalculatedAt: updated,
      totals,
      history: history.reverse(),
    };
  });
